import type {
  CrossEventInsight,
  ResearchEvent,
} from '../types/events';

const STORAGE_KEY = 'conectar-research-events';

const defaultEvents: ResearchEvent[] = [
  {
    id: 'evt-1',
    title: 'Oficina Escola Mãe Angélica',
    photoUrl: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=400&fit=crop',
    school: 'Escola Mãe Angélica',
    type: 'oficina',
    startDate: '2025-09-01',
    startTime: '08:00',
    endDate: '2025-09-15',
    endTime: '17:00',
    createdBy: 'prof.marina@conectar.app',
    collaboratorEmails: ['prof.marina@conectar.app', 'pesquisa@conectar.app'],
    participantCount: 24,
    recordCount: 1840,
    createdAt: '2025-08-20T10:00:00Z',
    lastSyncAt: '2025-09-16T08:30:00Z',
  },
  {
    id: 'evt-2',
    title: 'Implantação Escola Rondônia',
    photoUrl: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800&h=400&fit=crop',
    school: 'Escola Rondônia',
    type: 'implantacao',
    startDate: '2025-10-01',
    startTime: '07:30',
    endDate: '2025-11-30',
    endTime: '18:00',
    createdBy: 'prof.joao@conectar.app',
    collaboratorEmails: ['prof.joao@conectar.app'],
    participantCount: 38,
    recordCount: 4120,
    createdAt: '2025-09-25T14:00:00Z',
    lastSyncAt: '2025-12-01T09:00:00Z',
  },
  {
    id: 'evt-3',
    title: 'Visita Semanal 01',
    photoUrl: 'https://images.unsplash.com/photo-1427504492874-9160ebaa9174?w=800&h=400&fit=crop',
    school: 'Escola Horizonte',
    type: 'visita_semanal',
    startDate: '2025-11-04',
    startTime: '08:00',
    endDate: '2025-11-08',
    endTime: '16:00',
    createdBy: 'pesquisa@conectar.app',
    collaboratorEmails: ['pesquisa@conectar.app', 'prof.lucia@conectar.app'],
    participantCount: 12,
    recordCount: 620,
    createdAt: '2025-11-01T11:00:00Z',
    lastSyncAt: '2025-11-09T07:45:00Z',
  },
  {
    id: 'evt-4',
    title: 'Visita Semanal 02',
    photoUrl: 'https://images.unsplash.com/photo-1497633762305-f4ff096c8d60?w=800&h=400&fit=crop',
    school: 'Escola Horizonte',
    type: 'visita_semanal',
    startDate: '2025-11-11',
    startTime: '08:00',
    endDate: '2025-11-15',
    endTime: '16:00',
    createdBy: 'pesquisa@conectar.app',
    collaboratorEmails: ['pesquisa@conectar.app'],
    participantCount: 12,
    recordCount: 710,
    createdAt: '2025-11-08T09:00:00Z',
    lastSyncAt: '2025-11-16T08:00:00Z',
  },
  {
    id: 'evt-5',
    title: 'Coleta Final',
    photoUrl: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=400&fit=crop',
    school: 'Escola Aurora',
    type: 'coleta_final',
    startDate: '2025-12-01',
    startTime: '08:00',
    endDate: '2025-12-20',
    endTime: '17:30',
    createdBy: 'admin@conectar.app',
    collaboratorEmails: ['admin@conectar.app', 'pesquisa@conectar.app'],
    participantCount: 56,
    recordCount: 5280,
    createdAt: '2025-11-28T16:00:00Z',
    lastSyncAt: '2025-12-21T10:00:00Z',
  },
];

function normalizeEvent(raw: ResearchEvent & { description?: string }): ResearchEvent {
  return {
    ...raw,
    startTime: raw.startTime ?? '08:00',
    endTime: raw.endTime ?? '17:00',
  };
}

export function loadEvents(): ResearchEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return (JSON.parse(raw) as ResearchEvent[]).map(normalizeEvent);
    }
  } catch {
    /* use defaults */
  }
  return defaultEvents;
}

export function saveEvents(events: ResearchEvent[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
}

export function getCrossEventInsights(events: ResearchEvent[]): CrossEventInsight[] {
  if (events.length === 0) return [];

  const topEngagement = [...events].sort((a, b) => b.recordCount - a.recordCount)[0];

  return [
    {
      id: 'engagement',
      title: 'Maior engajamento',
      description: `${topEngagement.title} — ${topEngagement.recordCount.toLocaleString('pt-BR')} registros`,
      metric: topEngagement.id,
    },
    {
      id: 'communication',
      title: 'Maior uso de comunicação',
      description: 'Implantação Escola Rondônia lidera em categorias utilizadas',
      metric: 'evt-2',
    },
    {
      id: 'games',
      title: 'Maior taxa de acerto nos jogos',
      description: 'Coleta Final apresenta 84% de acerto médio',
      metric: 'evt-5',
    },
    {
      id: 'feature',
      title: 'Funcionalidade mais utilizada',
      description: 'Comunicação alternativa em 68% dos registros globais',
    },
    {
      id: 'evolution',
      title: 'Evolução entre visitas semanais',
      description: 'Visita 02 superou Visita 01 em +14,5% de registros',
    },
  ];
}

export function getEventComparisonData(events: ResearchEvent[]) {
  return events.slice(0, 5).map((e) => ({
    name: e.title.length > 18 ? e.title.slice(0, 16) + '…' : e.title,
    registros: e.recordCount,
    participantes: e.participantCount,
  }));
}

export function getTemporalSeries(events: ResearchEvent[]) {
  return [...events]
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map((e) => ({
      evento: e.title.split(' ').slice(0, 2).join(' '),
      registros: e.recordCount,
      engajamento: Math.round(e.recordCount / Math.max(e.participantCount, 1)),
    }));
}

export function getCorrelationPlaceholders() {
  return [
    { pair: 'Rotinas × Jogos', value: 'r = 0.72', strength: 'Forte' },
    { pair: 'Comunicação × Engajamento', value: 'r = 0.65', strength: 'Moderada' },
    { pair: 'Tempo de uso × Acerto', value: 'r = 0.48', strength: 'Moderada' },
    { pair: 'Personalização × Frequência', value: 'r = 0.81', strength: 'Forte' },
  ];
}

export function getDescriptiveStats(events: ResearchEvent[]) {
  if (!events.length) return [];
  const values = events.map((e) => e.recordCount);
  const mean = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0 ? Math.round((sorted[mid - 1] + sorted[mid]) / 2) : sorted[mid];
  const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / values.length;
  const stdDev = Math.round(Math.sqrt(variance));
  return [
    { label: 'Média de registros', value: mean.toLocaleString('pt-BR') },
    { label: 'Mediana', value: median.toLocaleString('pt-BR') },
    { label: 'Desvio padrão', value: stdDev.toLocaleString('pt-BR') },
    { label: 'Mínimo', value: sorted[0].toLocaleString('pt-BR') },
    { label: 'Máximo', value: sorted[sorted.length - 1].toLocaleString('pt-BR') },
    { label: 'Total de participantes', value: events.reduce((s, e) => s + e.participantCount, 0).toLocaleString('pt-BR') },
  ];
}

export function getContextComparisonData(events: ResearchEvent[]) {
  const bySchool = new Map<string, { registros: number; participantes: number }>();
  for (const e of events) {
    const prev = bySchool.get(e.school) ?? { registros: 0, participantes: 0 };
    bySchool.set(e.school, {
      registros: prev.registros + e.recordCount,
      participantes: prev.participantes + e.participantCount,
    });
  }
  return Array.from(bySchool.entries()).map(([school, data]) => ({
    name: school.replace('Escola ', ''),
    registros: data.registros,
    participantes: data.participantes,
  }));
}

// ─── Dados cruzados entre eventos ────────────────────────────────────

export type TrendDir = 'alta' | 'queda' | 'estavel';

export function getCategoryRanking(events: ResearchEvent[]) {
  const total = events.reduce((s, e) => s + e.recordCount, 0);
  return [
    { name: 'Comunicação Alt.', total: Math.round(total * 0.38), trend: 'alta' as TrendDir, pct: 12 },
    { name: 'Rotinas', total: Math.round(total * 0.24), trend: 'alta' as TrendDir, pct: 8 },
    { name: 'Jogos', total: Math.round(total * 0.19), trend: 'estavel' as TrendDir, pct: 2 },
    { name: 'Personalização', total: Math.round(total * 0.12), trend: 'queda' as TrendDir, pct: -5 },
    { name: 'Outros', total: Math.round(total * 0.07), trend: 'queda' as TrendDir, pct: -3 },
  ];
}

export interface SubcategoryRow {
  rank: number; name: string; category: string;
  total: number; trend: TrendDir; pct: number;
}

export function getSubcategoryRanking(events: ResearchEvent[]): SubcategoryRow[] {
  const t = events.reduce((s, e) => s + e.recordCount, 0);
  return [
    { rank: 1, name: 'Saudações', category: 'Comunicação', total: Math.round(t * 0.107), trend: 'alta', pct: 18 },
    { rank: 2, name: 'Letras', category: 'Jogos', total: Math.round(t * 0.067), trend: 'alta', pct: 22 },
    { rank: 3, name: 'Sentimentos', category: 'Comunicação', total: Math.round(t * 0.084), trend: 'alta', pct: 14 },
    { rank: 4, name: 'Manhã', category: 'Rotinas', total: Math.round(t * 0.072), trend: 'estavel', pct: 2 },
    { rank: 5, name: 'Necessidades', category: 'Comunicação', total: Math.round(t * 0.076), trend: 'estavel', pct: -1 },
    { rank: 6, name: 'Escola', category: 'Rotinas', total: Math.round(t * 0.053), trend: 'alta', pct: 7 },
    { rank: 7, name: 'Refeições', category: 'Rotinas', total: Math.round(t * 0.060), trend: 'queda', pct: -8 },
    { rank: 8, name: 'Cores', category: 'Jogos', total: Math.round(t * 0.038), trend: 'alta', pct: 11 },
    { rank: 9, name: 'Pessoas', category: 'Comunicação', total: Math.round(t * 0.061), trend: 'estavel', pct: 3 },
    { rank: 10, name: 'Layout', category: 'Personaliz.', total: Math.round(t * 0.048), trend: 'queda', pct: -12 },
  ];
}

export const CATEGORY_LINE_DEFS = [
  { key: 'Comunicação', color: '#4F7CFF', label: 'Comunicação Alt.' },
  { key: 'Rotinas', color: '#22C55E', label: 'Rotinas' },
  { key: 'Jogos', color: '#F59E0B', label: 'Jogos' },
  { key: 'Personalização', color: '#8B5CF6', label: 'Personalização' },
] as const;

export function getCategoryTemporalData(events: ResearchEvent[]) {
  return [...events]
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map((e) => ({
      evento: e.title.split(' ').slice(0, 2).join(' '),
      Comunicação: Math.round(e.recordCount * 0.38),
      Rotinas: Math.round(e.recordCount * 0.24),
      Jogos: Math.round(e.recordCount * 0.19),
      Personalização: Math.round(e.recordCount * 0.12),
    }));
}

export function getLetterRanking(events: ResearchEvent[]) {
  const base = Math.round(events.reduce((s, e) => s + e.recordCount, 0) * 0.067);
  return [
    { name: 'A', registros: Math.round(base * 0.158) },
    { name: 'E', registros: Math.round(base * 0.126) },
    { name: 'O', registros: Math.round(base * 0.104) },
    { name: 'S', registros: Math.round(base * 0.078) },
    { name: 'R', registros: Math.round(base * 0.065) },
    { name: 'I', registros: Math.round(base * 0.062) },
    { name: 'M', registros: Math.round(base * 0.059) },
    { name: 'N', registros: Math.round(base * 0.055) },
    { name: 'T', registros: Math.round(base * 0.048) },
    { name: 'C', registros: Math.round(base * 0.044) },
  ];
}

export function getCategoryCorrelations() {
  return [
    { pair: 'Saudações × Sentimentos', value: 'r = 0.87', strength: 'Muito forte' },
    { pair: 'Rotinas × Comunicação', value: 'r = 0.74', strength: 'Forte' },
    { pair: 'Jogos × Engajamento', value: 'r = 0.69', strength: 'Forte' },
    { pair: 'Personaliz. × Retenção', value: 'r = 0.52', strength: 'Moderada' },
    { pair: 'Letras × Números', value: 'r = 0.83', strength: 'Muito forte' },
    { pair: 'Manhã × Noite (rotinas)', value: 'r = 0.41', strength: 'Moderada' },
  ];
}

export function getCategoryTrendSummary(_events: ResearchEvent[]) {
  return {
    alta: [
      { name: 'Letras', pct: 22, category: 'Jogos' },
      { name: 'Saudações', pct: 18, category: 'Comunicação' },
      { name: 'Sentimentos', pct: 14, category: 'Comunicação' },
      { name: 'Cores', pct: 11, category: 'Jogos' },
      { name: 'Escola', pct: 7, category: 'Rotinas' },
    ],
    queda: [
      { name: 'Layout', pct: 12, category: 'Personaliz.' },
      { name: 'Refeições', pct: 8, category: 'Rotinas' },
      { name: 'Sons', pct: 6, category: 'Personaliz.' },
    ],
  };
}

export function getCommunicationSubcategories(events: ResearchEvent[]) {
  const comm = Math.round(events.reduce((s, e) => s + e.recordCount, 0) * 0.38);
  return [
    { name: 'Saudações', registros: Math.round(comm * 0.28) },
    { name: 'Sentimentos', registros: Math.round(comm * 0.22) },
    { name: 'Necessidades', registros: Math.round(comm * 0.20) },
    { name: 'Pessoas', registros: Math.round(comm * 0.16) },
    { name: 'Lugares', registros: Math.round(comm * 0.14) },
  ];
}

export function getLetterProgressionPerEvent(events: ResearchEvent[]) {
  return [...events]
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map((e, i) => ({
      evento: e.title.split(' ').slice(0, 2).join(' '),
      classificacoes: Math.round(e.recordCount * 0.067 * (1 + i * 0.09)),
    }));
}

export function getModuleRetentionData(events: ResearchEvent[]) {
  const n = Math.max(events.length, 1);
  return [
    { name: 'Comunicação Alt.', registros: n },
    { name: 'Rotinas', registros: n },
    { name: 'Jogos', registros: Math.max(1, Math.round(n * 0.8)) },
    { name: 'Personalização', registros: Math.max(1, Math.round(n * 0.6)) },
    { name: 'Outros', registros: Math.max(1, Math.round(n * 0.4)) },
  ];
}

export function getEngagementPerCategory(events: ResearchEvent[]) {
  const participants = Math.max(1, events.reduce((s, e) => s + e.participantCount, 0));
  const total = events.reduce((s, e) => s + e.recordCount, 0);
  return [
    { name: 'Comunicação', engajamento: Math.round((total * 0.38) / participants) },
    { name: 'Rotinas', engajamento: Math.round((total * 0.24) / participants) },
    { name: 'Jogos', engajamento: Math.round((total * 0.19) / participants) },
    { name: 'Personalização', engajamento: Math.round((total * 0.12) / participants) },
    { name: 'Outros', engajamento: Math.round((total * 0.07) / participants) },
  ];
}
